// src/features/auth/queries/workspaceAccessQueries.ts
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { authQueryKeys } from './authQueries';
import { useAuthQuery } from './authQueries';
import { AuthCacheManager } from '../AuthCacheManager';

// Firestore から当該ユーザーの当該ワークスペースへのアクセス可否を取得
const fetchWorkspaceAccess = async (workspaceId: string, userId: string): Promise<boolean> => {
  const fetchStartTime = performance.now();

  try {
    // ここは既存実装に合わせています。TTL/メモ化は AuthCacheManager 側で。
    const hasAccess = await AuthCacheManager.checkAccessFromFirestore(workspaceId, userId);
    const fetchEndTime = performance.now();

    return hasAccess;
  } catch (error) {
    const fetchEndTime = performance.now();
    console.error('🔒 [WorkspaceAccess] アクセス権限チェックエラー', {
      workspaceId,
      uid: userId,
      error,
      fetchTime: fetchEndTime - fetchStartTime
    });
    return false;
  }
};

/**
 * ワークスペースアクセス権限を取得するクエリフック
 * uidをクエリキーに含めることで、ユーザー切替時のキャッシュ取り違えを防ぐ
 */
export const useWorkspaceAccessQuery = (workspaceId: string, enabled: boolean = true) => {
  const { data: user } = useAuthQuery();
  const uid = user?.uid;

  const result = useQuery<boolean>({
    queryKey: authQueryKeys.workspaceAccess(workspaceId, uid),
    queryFn: () => fetchWorkspaceAccess(workspaceId, uid as string),
    enabled: enabled && !!uid,
    staleTime: 5 * 60 * 1000, // 5分
    gcTime: 30 * 60 * 1000,   // 30分
    retry: 1,
  });

  // クエリ結果の確定時のみログ出力（最適化）
  React.useEffect(() => {
    if (result.data !== undefined && !result.isLoading && !result.isError) {}
  }, [result.data, workspaceId, uid]); // 不要な依存配列を削除

  return result;
};

/**
 * 複数のワークスペースアクセス権限を一括取得するクエリフック
 * 効率的な権限チェックのために使用
 */
export const useMultipleWorkspaceAccessQuery = (workspaceIds: string[], enabled: boolean = true) => {
  const { data: user } = useAuthQuery();
  const uid = user?.uid;

  return useQuery<Record<string, boolean>>({
    queryKey: ['auth', 'multipleWorkspaceAccess', workspaceIds.sort(), uid ?? 'anon'],
    queryFn: async () => {
      if (!uid) return {};
      
      const results: Record<string, boolean> = {};
      const promises = workspaceIds.map(async (workspaceId) => {
        const hasAccess = await fetchWorkspaceAccess(workspaceId, uid);
        results[workspaceId] = hasAccess;
      });
      
      await Promise.all(promises);
      return results;
    },
    enabled: enabled && !!uid && workspaceIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5分
    gcTime: 30 * 60 * 1000,   // 30分
    retry: 1,
  });
};
